import { email, required, useRecordContext, useTranslate } from "ra-core";
import type { FocusEvent, ClipboardEventHandler } from "react";
import { useFormContext } from "react-hook-form";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { BooleanInput } from "@/components/admin/boolean-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { RadioButtonGroupInput } from "@/components/admin/radio-button-group-input";
import { SelectInput } from "@/components/admin/select-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { NumberInput } from "@/components/admin/number-input";
import { DateInput } from "@/components/admin/date-input";

import { isLinkedinUrl } from "../misc/isLinkedInUrl";
import {
  contactGender,
  translateContactGenderLabel,
  translatePersonalInfoTypeLabel,
} from "./contactGender";
import type { MYLESContact, Sale } from "../types";
import { Avatar } from "./Avatar";
import { AutocompleteCompanyInput } from "../companies/AutocompleteCompanyInput.tsx";
import { ScoreBadge } from "@/components/atomic-crm/scoring/ScoreBadge";

export const ContactInputs = () => {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-2 p-1 relative md:static">
      <div className="absolute top-0 right-1 md:static">
        <Avatar />
      </div>
      <div className="flex gap-10 md:gap-6 flex-col md:flex-row">
        <div className="flex flex-col gap-10 flex-1">
          <ContactIdentityInputs />
          <ContactPositionInputs />
        </div>
        {isMobile ? null : (
          <Separator orientation="vertical" className="flex-shrink-0" />
        )}
        <div className="flex flex-col gap-10 flex-1">
          <ContactPersonalInformationInputs />
          <ContactMiscInputs />
        </div>
      </div>
      <Separator className="my-2" />
      <div className="flex gap-10 md:gap-6 flex-col md:flex-row">
        <div className="flex flex-col gap-10 flex-1">
          <ContactRoleFunctionInputs />
          <ContactScoringProfileInputs />
        </div>
        {isMobile ? null : (
          <Separator orientation="vertical" className="flex-shrink-0" />
        )}
        <div className="flex flex-col gap-10 flex-1">
          <ContactStatusOwnershipInputs />
        </div>
      </div>
    </div>
  );
};

const ContactIdentityInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.identity")}
      </h6>
      <RadioButtonGroupInput
        label={false}
        row
        source="gender"
        choices={contactGender}
        helperText={false}
        optionText={(choice) => translateContactGenderLabel(choice, translate)}
        translateChoice={false}
        optionValue="value"
        defaultValue={contactGender[0].value}
      />
      <TextInput source="first_name" validate={required()} helperText={false} />
      <TextInput source="last_name" validate={required()} helperText={false} />
    </div>
  );
};

const ContactPositionInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.position")}
      </h6>
      <TextInput source="title" helperText={false} />
      <ReferenceInput source="company_id" reference="companies" perPage={10}>
        <AutocompleteCompanyInput label="resources.contacts.fields.company_id" />
      </ReferenceInput>
    </div>
  );
};

const ContactPersonalInformationInputs = () => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const personalInfoTypes = [
    {
      id: "Work",
      name: translatePersonalInfoTypeLabel("Work", translate),
    },
    {
      id: "Home",
      name: translatePersonalInfoTypeLabel("Home", translate),
    },
    {
      id: "Other",
      name: translatePersonalInfoTypeLabel("Other", translate),
    },
  ];

  // set first and last name based on email
  const handleEmailChange = (email: string) => {
    const { first_name, last_name } = getValues();
    if (first_name || last_name || !email) return;
    const [first, last] = email.split("@")[0].split(".");
    setValue("first_name", first.charAt(0).toUpperCase() + first.slice(1));
    setValue(
      "last_name",
      last ? last.charAt(0).toUpperCase() + last.slice(1) : "",
    );
  };

  const handleEmailPaste: ClipboardEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (e) => {
    const email = e.clipboardData?.getData("text/plain");
    handleEmailChange(email);
  };

  const handleEmailBlur = (
    e: FocusEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const email = e.target.value;
    handleEmailChange(email);
  };

  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.personal_info")}
      </h6>
      <ArrayInput source="email_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="email"
            className="w-full"
            helperText={false}
            label={false}
            placeholder={translate("resources.contacts.fields.email")}
            validate={email()}
            onPaste={handleEmailPaste}
            onBlur={handleEmailBlur}
          />
          <SelectInput
            source="type"
            helperText={false}
            label={false}
            optionText="name"
            choices={personalInfoTypes}
            defaultValue="Work"
            className="w-24 min-w-24"
          />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="phone_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="number"
            className="w-full"
            helperText={false}
            label={false}
            placeholder={translate("resources.contacts.fields.phone_number")}
          />
          <SelectInput
            source="type"
            helperText={false}
            label={false}
            optionText="name"
            choices={personalInfoTypes}
            defaultValue="Work"
            className="w-24 min-w-24"
          />
        </SimpleFormIterator>
      </ArrayInput>
      <TextInput
        source="linkedin_url"
        helperText={false}
        validate={isLinkedinUrl}
      />
    </div>
  );
};

const ContactMiscInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.misc")}
      </h6>
      <TextInput source="background" multiline helperText={false} />
      <BooleanInput source="has_newsletter" helperText={false} />
      <ReferenceInput
        reference="sales"
        source="sales_id"
        sort={{ field: "last_name", order: "ASC" }}
        filter={{
          "disabled@neq": true,
        }}
      >
        <SelectInput
          helperText={false}
          optionText={saleOptionRenderer}
          validate={required()}
        />
      </ReferenceInput>
    </div>
  );
};

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;

// ============================================================
// MYLES extended field sections
// ============================================================

const ContactRoleFunctionInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.role_function", {
          _: "Role & Function",
        })}
      </h6>
      <SelectInput
        source="function_role"
        helperText={false}
        choices={[
          { id: "Head of PM", name: "Head of PM" },
          { id: "Head of Marketing", name: "Head of Marketing" },
          {
            id: "Head of Business Division",
            name: "Head of Business Division",
          },
          { id: "Head of Strategic PM", name: "Head of Strategic PM" },
          { id: "Other", name: "Other" },
        ]}
      />
      <TextInput source="seniority_level" helperText={false} />
      <NumberInput source="tenure_years" helperText={false} min={0} />
      <DateInput source="tenure_start_date" helperText={false} />
      <BooleanInput source="is_budget_owner" helperText={false} />
      <SelectInput
        source="budget_scope"
        helperText={false}
        choices={[
          { id: "Full P&L", name: "Full P&L" },
          { id: "Significant authority", name: "Significant authority" },
          { id: "Partial influence", name: "Partial influence" },
        ]}
      />
    </div>
  );
};

const ContactScoringProfileInputs = () => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.scoring_profile", {
          _: "Scoring Profile",
        })}
      </h6>
      <SelectInput
        source="mint_background"
        helperText={false}
        choices={[
          { id: "MINT degree", name: "MINT degree" },
          { id: "Technical role", name: "Technical role" },
          { id: "None", name: "None" },
        ]}
      />
      <SelectInput
        source="innovation_experience"
        helperText={false}
        choices={[
          { id: "Strong", name: "Strong" },
          { id: "Moderate", name: "Moderate" },
          { id: "None", name: "None" },
        ]}
      />
      <SelectInput
        source="linkedin_activity"
        helperText={false}
        choices={[
          { id: "Very active", name: "Very active" },
          { id: "LI only", name: "LI only" },
          { id: "Public only", name: "Public only" },
          { id: "None", name: "None" },
        ]}
      />
      <SelectInput
        source="social_engagement"
        helperText={false}
        choices={[
          { id: "Yes", name: "Yes" },
          { id: "Somewhat", name: "Somewhat" },
          { id: "No", name: "No" },
        ]}
      />
    </div>
  );
};

const ContactStatusOwnershipInputs = () => {
  const translate = useTranslate();
  const record = useRecordContext<MYLESContact>();
  return (
    <div className="flex flex-col gap-4">
      <h6 className="text-lg font-semibold">
        {translate("resources.contacts.field_categories.status_ownership", {
          _: "Status & Ownership",
        })}
      </h6>
      <SelectInput
        source="contact_status"
        helperText={false}
        choices={[
          { id: "new", name: "New" },
          { id: "qualified", name: "Qualified" },
          { id: "contacted", name: "Contacted" },
          { id: "in_conversation", name: "In Conversation" },
          { id: "won", name: "Won" },
          { id: "lost", name: "Lost" },
          { id: "email_discovered", name: "Email Discovered" },
        ]}
      />
      <SelectInput
        source="source_origin"
        helperText={false}
        choices={[
          { id: "hubspot", name: "HubSpot" },
          { id: "pipedrive", name: "Pipedrive" },
          { id: "linkedin", name: "LinkedIn" },
          { id: "outlook", name: "Outlook" },
          { id: "manual", name: "Manual" },
        ]}
      />
      <ReferenceInput
        reference="sales"
        source="owner_id"
        sort={{ field: "last_name", order: "ASC" }}
        filter={{ "disabled@neq": true }}
      >
        <SelectInput helperText={false} optionText={saleOptionRenderer} />
      </ReferenceInput>
      {record &&
        (record.current_score != null || record.current_tier != null) && (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">
              {translate("resources.contacts.fields.current_score", {
                _: "Score",
              })}
            </span>
            <ScoreBadge
              score={record.current_score}
              tier={record.current_tier}
            />
          </div>
        )}
    </div>
  );
};
